import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import css from './SectionHeroCustom.module.css';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

// Hero videos (loop silently, replace static images)
const HERO_VIDEOS = [
  '/static/hero/salon_hero_1.mp4',
  '/static/hero/salon_hero_2.mp4',
];

/**
 * Section component for a website's hero section
 * The Section Hero doesn't have any Blocks by default, all the configurations are made in the Section Hero settings
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'hero'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionHeroCustom = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName || css.root)}
      sectionContentClassName={css.sectionContent}
      appearance={null}
      options={fieldOptions}
    >
      <div className={css.topSectionWrapper}>
        {/* Top Section: Light Green Background with Title & Description */}
        <div className={css.topSection}>
          <div className={css.contentWrapper}>
            {hasHeaderFields ? (
              <header className={classNames(defaultClasses.sectionDetails, css.headerContent)}>
                <Field data={title} className={defaultClasses.title} options={fieldOptions} />
                <Field
                  data={description}
                  className={defaultClasses.description}
                  options={fieldOptions}
                />
              </header>
            ) : null}

            {/* Search Bar Section */}
            <div className={css.searchBarWrapper}>
              <Field
                data={callToAction}
                className={defaultClasses.ctaButton}
                options={fieldOptions}
              />
            </div>
          </div>
        </div>

        {/* Bottom Section: Looping hero video */}
        <div className={css.bottomSection}>
          {HERO_VIDEOS.map((src, index) => (
            <video
              key={src}
              className={classNames(css.backgroundImage, {
                [css.activeImage]: index === currentVideoIndex,
              })}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              onEnded={() =>
                setCurrentVideoIndex(prev => (prev + 1) % HERO_VIDEOS.length)
              }
            >
              <source src={src} type="video/mp4" />
            </video>
          ))}
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionHeroCustom;
